import { useTranslation } from 'react-i18next';

/**
 * Кольцо дня — главный прибор экрана «Сегодня».
 * Бренд-правило (и явная правка Алекса): внутри кольца ТОЛЬКО большие
 * цифры по центру и «ккал» строго под ними — никаких других подписей;
 * без насечек и делений — чистая дуга. Смысл «осталось» живет снаружи
 * кольца (подпись карточки в Today.tsx).
 */

interface DayRingProps {
  consumedKcal: number;
  targetKcal: number;
}

const SIZE = 232;
const STROKE = 12;
const R = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * R;

export function DayRing({ consumedKcal, targetKcal }: DayRingProps): JSX.Element {
  const { t } = useTranslation();
  const remaining = Math.max(0, Math.round(targetKcal - consumedKcal));
  const progress = targetKcal > 0 ? Math.min(1, consumedKcal / targetKcal) : 0;
  const over = consumedKcal > targetKcal;

  return (
    <div className="dayring" role="img" aria-label={`${remaining} ${t('today.kcalUnit')}`}>
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`}>
        {/* Трек: тонкая, почти невидимая подложка. */}
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={R}
          fill="none"
          stroke="var(--line-1)"
          strokeWidth={STROKE}
        />
        {/* Дуга прогресса: старт сверху, свечение как у прибора. */}
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={R}
          fill="none"
          stroke={over ? 'var(--warn)' : 'var(--accent)'}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={CIRCUMFERENCE * (1 - progress)}
          transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
          style={{ filter: 'drop-shadow(0 0 10px var(--accent-glow))' }}
        />
      </svg>
      <div className="dayring-center">
        <span className="type-display-64 dayring-value">{remaining}</span>
        <span className="type-micro-11 dayring-unit">{t('today.kcalUnit')}</span>
      </div>
    </div>
  );
}
